import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Box, InputAdornment, makeStyles, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { FormikProps } from 'formik';
import Highcharts from 'highcharts';
import { calculateBalanceAsync } from '../../../../store/calculate';
import QuestionnaireScreen, { deserializeResponses, serializeResponses } from '../../QuestionnaireScreen';
import { Body } from '../../GridLayout';
import { useRiskProfile, useEmployed, usePlanningToWithdraw } from './BeginQuestions';
import Popup from '../../../containers/Popup';
import WhiteTextField from '../../WhiteTextField';
import Select from '../../Select';
import ThousandsFormat from '../../ThousandsFormat';
import PercentageFormat from '../../PercentageFormat';
import { useSharedStyles, ProductStyles } from '../../../../common/sharedStyles';
import WhiteTextBox from '../../WhiteTextBox';
import { Colors, HelpTooltip, SubtitleText, TitleText } from '../../../controls/helpers';
import useApplications from '../../../../hooks/useApplications';
import { Application, esctTaxBands, riskOptions } from '../../../../common/types';
import Chart, { Series, defaultMarker } from '../../../controls/helpers/Chart';
import { CalculateBalanceQuery, SchemeType } from '../../../../api/apiClients';
import { EmploymentStatus } from '../../../kiwiSaverCalculator/KiwiSaverCalculator';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getAge, getIncome, getKiwiSaverBalance } from './KiwiSaverCalculator';
import { usePlanningToWithdrawWb } from './WealthBuilder';
import { CurrencyTextInput, NumberTextInput } from '../../../controls/inputs';

const frequencies = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually'
};

const questions = {
  employerContribution: 'Employer contribution percent',
  yourContribution: 'Your contribution percent',
  wealthBuilderContribution: 'Wealth Builder contribution percent',
  income: 'Income',
  frequency: 'Contribution frequency',
  kiwiSaverDollarContribution: 'KiwiSaver contribution amount',
  yourDollarContribution: 'Your contribution amount',
  wealthBuilderDollarContribution: 'Wealth Builder contribution amount'
};

const useDefaultSettings = (employed?: EmploymentStatus, annualIncome?: number, age?: number, kiwiSaverBalance?: number) => ({
  ageInScenario: age || 25,
  retirementAge: 65,
  employmentStatus: employed || EmploymentStatus.Employed,
  kiwiSaverBalance: kiwiSaverBalance || 0,
  grossAnnualIncome: annualIncome || (employed === EmploymentStatus.SelfEmployed ? 100000 : 75000),
  contributionRate: 0.03,
  employerContribution: 0.03,
  wealthBuilderContribution: 0.03,
  kiwiSaverVoluntaryAnnualContribution: employed === EmploymentStatus.SelfEmployed ? 3000 : 0,
  schemeType: SchemeType.LifeCycle
});

const ContributionCalculator = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { calculateBalances: kiwiSaverBalances } = useAppSelector(state => state.calculate);
  const { responses } = useAppSelector(state => state.clientQuestionnaires);
  const sharedClasses = useSharedStyles();
  const [popupShown, setPopupShown] = useState<boolean>(false);
  const [prevValues, setPrevValues] = useState<any>();
  const plansToWithdrawKs = usePlanningToWithdraw();
  const plansToWithdrawWb = usePlanningToWithdrawWb();
  const lowRiskProfile = useRiskProfile() === riskOptions.low;
  const employed = useEmployed();
  const annualIncome = getIncome();
  const age = getAge();
  const kiwiSaverBalance = getKiwiSaverBalance();
  const applications = useApplications();
  const isKsOnly = applications.length === 1 && applications.includes(Application.KiwiSaver);
  const [series, setSeries] = useState<Series[]>([]);
  const defaultSettings = useDefaultSettings(employed ? EmploymentStatus.Employed : EmploymentStatus.SelfEmployed, annualIncome, age, kiwiSaverBalance);

  useEffect(() => {
    if (!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile) {
      calculate(defaultSettings);

      let timeout: number | undefined;
      const handleResize = () => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => { Highcharts.charts.forEach(chart => !!chart && chart.reflow()); }, 333);
      }

      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const calculate = (values: any) => {
    if (!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile) {
      setPrevValues(values);
      setSeries([]);

      const request: CalculateBalanceQuery = {
        ageInScenario: values.ageInScenario,
        grossAnnualIncome: values.grossAnnualIncome,
        kiwiSaverBalance: values.kiwiSaverBalance,
        kiwiSaverEmployeeContribution: values.employmentStatus === EmploymentStatus.Employed ? values.contributionRate : 0,
        kiwiSaverEmployerContribution: values.employmentStatus === EmploymentStatus.Employed ? values.employerContribution : 0,
        kiwiSaverVoluntaryAnnualContribution: values.employmentStatus === EmploymentStatus.Employed ? 0 : values.grossAnnualIncome ? parseFloat(values.grossAnnualIncome.toString().replace(/,/g, '')) * values.wealthBuilderContribution : 0,
        wealthBuilderContribution: values.wealthBuilderContribution,
        retirementAge: defaultSettings.retirementAge,
        schemeType: defaultSettings.schemeType
      };

      dispatch(calculateBalanceAsync([request]));
    }
  };

  const initialValues = !responses ? {} : {
    employerContribution: 3,
    yourContribution: isKsOnly ? 6 : 3,
    wealthBuilderContribution: isKsOnly ? undefined : employed ? 3 : 6,
    frequency: 'weekly',
    income: annualIncome,
    age,
    kiwiSaverBalance,
    kiwiSaverDollarContribution: undefined,
    yourDollarContribution: undefined,
    wealthBuilderDollarContribution: undefined
  };

  return <QuestionnaireScreen
    initialValues={initialValues}
    deserializeResponses={deserializeResponses(questions)}
    serializeResponses={serializeResponses(questions)}>
    {(formik: FormikProps<any>) => {
      const { age, kiwiSaverBalance, frequency, employerContribution, yourContribution, yourDollarContribution, wealthBuilderContribution, kiwiSaverDollarContribution, income } = formik.values;
      const esctBand = esctTaxBands.find(value => Number(income) <= value?.UpTo && value.TaxRate);
      const taxedEmployerContribution = esctBand ? parsePercent(employerContribution) * esctBand.TaxRate : 0;
      const totalPercent = employed ? parsePercent(employerContribution) + parsePercent(yourContribution) + parsePercent(wealthBuilderContribution) : totalSavingsPercent(formik.values);
      const governmentContributionAmount = employed ? yourDollarContribution : governmentContribution(parseFloat(kiwiSaverDollarContribution) || 0, frequency);
      const totalSavingsAmount = totalSavings(formik.values);

      const getContribution = (percent: string | number, tax?: number) => {
        const ratio = parsePercent(percent) / 100;
        const annualAmount = (Number(income) || 0) * ratio;
        const frequencyAmount = annualAmount / (periodMultiplier[frequency] || 1);
        return (frequencyAmount - (tax ?? 0)).toFixed(2);
      }

      const getESCTDeductedEmployerContribution = (percent: string | number) => {
        const contributionAmount = Number(getContribution(percent));
        const esctAmount = esctBand ? contributionAmount * esctBand.TaxRate : 0;
        return (contributionAmount - esctAmount).toFixed(2);
      }

      const getSelfEmployedContribution = () => {
        if (!employed) {
          formik.setFieldValue('kiwiSaverDollarContribution', getContribution(isKsOnly ? 9 : 3));
          if (!isKsOnly) formik.setFieldValue('wealthBuilderDollarContribution', getContribution(6));
        }
      }

      useEffect(() => {
        getSelfEmployedContribution();
      }, [frequency]);

      useEffect(() => {
        if (!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile) {
          calculate({
            ...defaultSettings,
            kiwiSaverBalance,
            ageInScenario: age,
            grossAnnualIncome: income,
            wealthBuilderContribution: wealthBuilderContribution / 100,
            contributionRate: yourContribution / 100,
            employerContribution: employerContribution / 100
          });
        }
      }, [wealthBuilderContribution, employerContribution, yourContribution]);

      useEffect(() => {
        if (!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile) {
          if (kiwiSaverBalances?.length === 1) {
            setSeries([]);
            setSeries(s => [...s, {
              type: 'areaspline',
              name: 'Wealth Builder',
              color: ProductStyles.WealthBuilder.color,
              data: [
                { y: 0 },
                { y: (kiwiSaverBalances[0].wealthBuilderBalance || 0) / 4 },
                { y: kiwiSaverBalances[0].wealthBuilderBalance || 0, marker: defaultMarker({ fillColor: ProductStyles.WealthBuilder.color }) }
              ],
            }, {
              type: 'areaspline',
              name: 'KiwiSaver',
              color: ProductStyles.KiwiSaver.color,
              data: [
                { y: 0 },
                { y: (kiwiSaverBalances[0].kiwiSaverBalance || 0) / 4 },
                { y: kiwiSaverBalances[0].kiwiSaverBalance || 0, marker: defaultMarker({ fillColor: ProductStyles.KiwiSaver.color }) }
              ],
            }]);
          }
        }
      }, [kiwiSaverBalances]);

      return <Body isNarrowView>
        <TitleText>{employed ? 'What does saving 9% mean for you?' : 'Estimate your savings rate using the contribution calculator.'}</TitleText>
        {plansToWithdrawKs && !popupShown &&
          <Popup onOkay={() => setPopupShown(true)}>
            <Typography>If you plan to use KiwiSaver to purchase your first home, check your eligibility for the KiwiSaver First Home Grant with your adviser before making any changes to your KiwiSaver contribution rate as this may affect your entitlement to the subsidy.</Typography>
          </Popup>
        }
        {!employed && <>
          {isKsOnly && <SubtitleText>Enter in your annual income and the frequency in which you'd like to contribute into our contribution calculator to find out what contributing 9% to your KiwiSaver would look like.</SubtitleText>}
          <SubtitleText>If you contribute $20 a week / $1,043 a year, the Government will contribute $521.43 to your KiwiSaver. Saving at least $20 a week is therefore a no-brainer.</SubtitleText>
          <SubtitleText>
            However, as you are self-employed,
            {isKsOnly ?
                      <> we recommend you top up your contribution rate to 9% using KiwiSaver.But first, talk to your adviser or nominated representative about saving options that aren't locked in. <a href='https://www.youtube.com/embed/Q60uiBsqlME?rel=0&modestbranding=1' target='_blank'>Click here</a> to learn more.</> :
              ` use Wealth Builder to top up your savings rate to 9% as it's not locked in.`
            }
          </SubtitleText>
        </>}
        {employed && <>
          {isKsOnly && <SubtitleText>Enter in your employer contribution, your contribution, your annual income and the frequency in which you'd like to contribute into our contribution calculator to find out what contributing 9% to your KiwiSaver would look like.</SubtitleText>}
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className={classes.tableCell}>
                  <Typography className={classes.textWithHelpIcon}>
                    Employer KiwiSaver contribution
                    <HelpTooltip title="Your employer usually contributes at least 3% of your gross earnings on top of your regular pay. However your employer must deduct tax, so the amount paid to your KiwiSaver scheme will be less than 3%." />
                  </Typography>
                </TableCell>
                <TableCell className={clsx(classes.tableCell, classes.percentCell)}>
                  <WhiteTextField
                    placeholder="0"
                    variant="outlined"
                    name="employerContribution"
                    className={classes.percentInput}
                    InputProps={{ endAdornment: <InputAdornment position="end" disablePointerEvents>%&emsp;&nbsp;&nbsp;</InputAdornment>, inputComponent: PercentageFormat, inputProps: { max: 10 } }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableCell}>
                  <Typography className={classes.textWithHelpIcon}>
                    Your KiwiSaver contribution
                    <HelpTooltip title="If you are employed, your KiwiSaver contributions will be deducted directly from your gross pay. You can choose a contribution rate of 3%, 4%, 6%, 8%, or 10%." />
                  </Typography>
                </TableCell>
                <TableCell className={clsx(classes.tableCell, classes.percentCell)}>
                  <Select noBlankOption hideExpandIcon name="yourContribution" options={{ '3': '3%', '4': '4%', '6': '6%', '8': '8%', '10': '10%' }} className={classes.percentSelect} />
                </TableCell>
              </TableRow>
              {!isKsOnly &&
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    <Typography className={classes.textWithHelpIcon}>
                      New Wealth Builder contribution
                      <HelpTooltip title="Wealth Builder: Use KiwiSaver to get to 6%. Then top up with Wealth Builder to get to 9%." />
                    </Typography>
                  </TableCell>
                  <TableCell className={clsx(classes.tableCell, classes.percentCell)} style={{ paddingBottom: 10 }}>
                    <WhiteTextField
                      placeholder="0"
                      variant="outlined"
                      name="wealthBuilderContribution"
                      className={classes.percentInput}
                      InputProps={{ endAdornment: <InputAdornment position="end" disablePointerEvents>%&emsp;&nbsp;&nbsp;</InputAdornment>, inputComponent: PercentageFormat, inputProps: { max: 99 } }}
                    />
                  </TableCell>
                </TableRow>
              }
              <TableRow>
                <TableCell className={clsx(classes.tableCell, classes.borderTop)}>
                  <Typography className={classes.totalLabel}>Total</Typography>
                </TableCell>
                <TableCell className={clsx(classes.tableCell, classes.borderTop, classes.totalPercentCell)} style={{ paddingRight: totalPercent >= 9 && totalPercent <= 100 ? 0 : 20 }} >
                  <Box display="flex" justifyContent="center">
                    <Typography className={classes.totalLabel}>{totalPercent.toFixed()}%</Typography>
                    {totalPercent >= 9 && totalPercent <= 100 ? <CheckCircleIcon className={classes.tickIcon} /> : <>&nbsp;&nbsp;&nbsp;</>}
                    {totalPercent > 100 && <CancelIcon className={classes.crossIcon} />}
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>}
        <br />
        {(isKsOnly || plansToWithdrawKs || plansToWithdrawWb || lowRiskProfile) && <Typography gutterBottom variant="h6">{employed ? 'Enter your income and how often you get paid to see what your contributions might look like.' : 'Enter your income and your preferred frequency to see what your contributions might look like.'}</Typography>}
        {!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile && <>
          <Typography gutterBottom variant="h6">Enter your age, income and current KiwiSaver balance to see what your balance at retirement could look like with Wealth Builder.</Typography>
          <Box my={1}>
            <NumberTextInput
              name="age"
              label="Age"
              mask="999"
              onBlur={() => prevValues?.ageInScenario != age && calculate({
                ...defaultSettings,
                kiwiSaverBalance: kiwiSaverBalance,
                ageInScenario: age,
                grossAnnualIncome: income,
                wealthBuilderContribution: wealthBuilderContribution / 100,
                contributionRate: yourContribution / 100,
                employerContribution: employerContribution / 100
              })}
            />
          </Box>
        </>}
        <Box my={1}>
          <CurrencyTextInput
            name="income"
            label="Annual income"
            onBlur={() => {
              getSelfEmployedContribution();
              prevValues?.grossAnnualIncome != income && calculate({
                ...defaultSettings,
                kiwiSaverBalance: kiwiSaverBalance,
                ageInScenario: age,
                grossAnnualIncome: income,
                wealthBuilderContribution: wealthBuilderContribution / 100,
                contributionRate: yourContribution / 100,
                employerContribution: employerContribution / 100
              });
            }}
          />
        </Box>
        {!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile &&
          <Box my={1}>
            <CurrencyTextInput
              name="kiwiSaverBalance"
              label="KiwiSaver balance"
              onBlur={() => prevValues?.kiwiSaverBalance != kiwiSaverBalance && calculate({
                ...defaultSettings,
                kiwiSaverBalance: kiwiSaverBalance,
                ageInScenario: age,
                grossAnnualIncome: income,
                wealthBuilderContribution: wealthBuilderContribution / 100,
                contributionRate: yourContribution / 100,
                employerContribution: employerContribution / 100
              })}
            />
          </Box>
        }
        {(isKsOnly || plansToWithdrawKs || plansToWithdrawWb || lowRiskProfile) && <Select noBlankOption name="frequency" label="Frequency" options={frequencies} />}
        <br />
        {!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile && <>
          <Typography variant="h6" style={{ color: ProductStyles.WealthBuilder.color, lineHeight: 0.75 }}>When Wealth Builder is added</Typography>
          <Typography variant="h6" color="textSecondary">with {wealthBuilderContribution ? wealthBuilderContribution : 0}% contribution p.a.</Typography>
          <Box position="relative" top={0} height={45}>
            <Box position="absolute" zIndex={3} top={15} left={15}>
              <Typography variant="h6" style={{ color: ProductStyles.KiwiSaver.color, fontSize: '1rem', lineHeight: 0.75 }}>KiwiSaver +</Typography>
              <Typography variant="h6" style={{ color: ProductStyles.WealthBuilder.color, fontSize: '1rem' }}>Wealth Builder</Typography>
            </Box>
            <Box position="absolute" zIndex={3} top={8} right={15}>
              <Box mb="7px" mr={0} p="5px 10px" bgcolor={ProductStyles.WealthBuilder.color}>
                <Typography variant="h6" style={{ color: Colors.White, fontSize: '1.35rem' }}>${Highcharts.numberFormat(kiwiSaverBalances?.length === 1 ? (kiwiSaverBalances[0].wealthBuilderBalance || 0) + (kiwiSaverBalances[0]?.kiwiSaverBalance || 0) : 0, 0, '.', ',')}</Typography>
              </Box>
            </Box>
          </Box>
          <Box position="relative">
            <Chart
              series={series}
              startLabel="Today"
              endLabel="At retirement"
              dataLabelOptions={{
                enabled: true,
                formatter() {
                  if (this.x !== 2) return '';
                  return `<span style="color:${this.color}; font-size:0.78rem">$${Highcharts.numberFormat(this.series.name === 'KiwiSaver' ? this.y! : kiwiSaverBalances?.length === 1 ? (kiwiSaverBalances[0].wealthBuilderBalance || 0) : 0, 0, '.', ',')}</span>`;
                }
              }}
            />
          </Box>
          <Box mt={1} textAlign="center">
            <Typography className={sharedClasses.smallPrint}>The calculator uses retirement savings projections from the Financial Markets Conduct Regulations 2014 Schedule 7A.</Typography>
          </Box>
        </>}
        <br />
        {(isKsOnly || plansToWithdrawKs || plansToWithdrawWb || lowRiskProfile) && <Typography gutterBottom variant="h6">Your {frequency === 'annually' ? 'annual' : (frequency || 'weekly')} contributions might look like this:</Typography>}
        {!isKsOnly && !plansToWithdrawKs && !plansToWithdrawWb && !lowRiskProfile && <>
          <Typography gutterBottom variant="h6">Select {employed ? 'how often you get paid' : 'your preferred frequency'} to see what your contributions could be.</Typography>
          <Select noBlankOption name="frequency" label="Frequency" options={frequencies} />
        </>}
        {employed && <>
          <WhiteTextBox label="Employer KiwiSaver contribution (ESCT deducted)" classes={{ textBox: classes.greyHighlight, label: classes.marginTop }}>
            <span className={classes.savingsValue}>
              $<ThousandsFormat displayType="text" value={getESCTDeductedEmployerContribution(employerContribution)} />
            </span>
          </WhiteTextBox>
          <WhiteTextBox label="Your KiwiSaver contribution" classes={{ textBox: classes.greyHighlight, label: classes.marginTop }}>
            $<ThousandsFormat displayType="text" value={getContribution(yourContribution)} />
          </WhiteTextBox>
          {!isKsOnly && <>
            <WhiteTextBox label="New Wealth Builder contribution" classes={{ label: clsx(classes.wbTextColor, classes.marginTop) }}>
              $<ThousandsFormat displayType="text" value={getContribution(wealthBuilderContribution)} />
            </WhiteTextBox>
            <Typography className={clsx(sharedClasses.smallPrint, classes.wbTextColor)} style={{ marginBottom: 10 }}>Apply for a Wealth Builder account and set up a regular contribution.</Typography>
          </>}
        </>}
        {!employed && <>
          <Box className={classes.marginTop}>
            <WhiteTextField
              dollarSignAdornment
              variant="outlined"
              name="kiwiSaverDollarContribution"
              label="KiwiSaver contribution (before tax)"
              InputProps={{ inputComponent: ThousandsFormat }}
            />
          </Box>
          <WhiteTextField
            disabled
            dollarSignAdornment
            variant="outlined"
            name="yourDollarContribution"
            label="Government KiwiSaver contribution"
            InputProps={{ inputComponent: ThousandsFormat }}
            value={governmentContributionAmount}
            textColor={Colors.Grey}
          />
          {!isKsOnly && <>
            <WhiteTextField
              dollarSignAdornment
              variant="outlined"
              name="wealthBuilderDollarContribution"
              label="New Wealth Builder contribution"
              labelColor={ProductStyles.WealthBuilder.color}
              InputProps={{ inputComponent: ThousandsFormat }}
              className={classes.hideMarginBottom}
            />
            <Typography className={clsx(sharedClasses.smallPrint, classes.wbTextColor)} style={{ marginBottom: 10 }}>Apply for a Wealth Builder account and set up a regular contribution.</Typography>
          </>}
        </>}
        <WhiteTextBox
          label={employed ? 'Total contribution' : 'Savings Rate'}
          classes={{ textBox: income && totalPercent >= 9 ? classes.greenHighlight : classes.greyHighlight, label: isKsOnly ? classes.marginTop : undefined }}
          endAdornment={income && totalPercent >= 9 && <CheckCircleIcon className={classes.tickIcon} />}>
          <b>
            <span className={classes.savingsValue}>
              $<ThousandsFormat displayType="text" value={employed ? getContribution(totalPercent - taxedEmployerContribution) : totalSavingsAmount} />
            </span>
            {!employed && <span className={classes.divider}>|</span>}
            {!employed && <span className={classes.savingsValue}>{totalPercent.toFixed()}%</span>}
          </b>
        </WhiteTextBox>
        {isKsOnly && employed &&
          <Box mt={3}>
            <SubtitleText>
              Before contributing 6% to your KiwiSaver, talk to your adviser about other saving options that aren't locked in.&nbsp;
              <a href="https://www.youtube.com/embed/Q60uiBsqlME?rel=0&modestbranding=1" target="_blank">Click here</a> to learn more about savings options that aren't locked in.
            </SubtitleText>
          </Box>
        }
        <Box my={3} textAlign="center" alignItems="center" alignSelf="center">
          <Typography className={sharedClasses.smallPrint}>The outcomes presented in the calculator are illustrative only{employed && <>, and reflect the impact of tax on contributions</>}.</Typography>
        </Box>
      </Body>
    }}
  </QuestionnaireScreen>
}

const round = (number: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.round((number + Number.EPSILON) * factor) / factor;
}

const parsePercent = (value: number | string) => {
  if (!value) return 0;
  const number = Number(value);
  if (!number || number < 0 || number >= 100) return 0;
  return number;
}

const totalSavingsPercent = (formValues: any) => {
  const total = totalSavings(formValues);
  const percent = (periodMultiplier[formValues.frequency as keyof typeof frequencies] * total) / formValues.income * 100;
  return round(percent || 0, 1);
}

const totalSavings = (formValues: any) => {
  const personal = parseFloat(formValues.kiwiSaverDollarContribution) || 0;
  const government = governmentContribution(personal, formValues.frequency);
  const wealthBuilder = parseFloat(formValues.wealthBuilderDollarContribution) || 0;
  return round(personal + government + wealthBuilder, 2);
}

const governmentContribution = (personalContribution: number, frequency: keyof typeof frequencies) => {
  if (!frequency) return 0;
  const maxPerYear = 521.43;
  const amount = Math.min(personalContribution / 2, maxPerYear / periodMultiplier[frequency]);
  return round(amount, 2);
}

const periodMultiplier: Record<string, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  annually: 1
};

const useStyles = makeStyles({
  tickIcon: {
    color: Colors.Green,
    marginLeft: 5
  },
  crossIcon: {
    color: Colors.Red
  },
  totalLabel: {
    fontWeight: 500
  },
  percentInput: {
    margin: 0,
    '& .MuiTypography-root': {
      color: Colors.Black
    },
    '& input': {
      textAlign: 'right'
    },
    '& .MuiInputAdornment-root .MuiTypography-root': {
      position: 'relative',
      left: -8
    }
  },
  percentSelect: {
    textAlign: 'right'
  },
  tableCell: {
    padding: 5,
    borderBottom: '0px solid transparent'
  },
  percentCell: {
    width: 106
  },
  borderTop: {
    borderTop: `1px solid ${Colors.Grey}`
  },
  totalPercentCell: {
    padding: 20,
    textAlign: 'center'
  },
  savingsValue: {},
  greenHighlight: {
    border: `solid 1px ${Colors.Green}`,
    '& $savingsValue': {
      color: Colors.Green
    }
  },
  greyHighlight: {
    border: `1px solid ${Colors.Grey}`
  },
  wbBorder: {
    marginBottom: 0,
    border: `1px solid ${ProductStyles.WealthBuilder.color}`
  },
  wbTextColor: {
    color: `${ProductStyles.WealthBuilder.color} !important`
  },
  marginTop: {
    marginTop: 15
  },
  divider: {
    padding: '0 12px'
  },
  textWithHelpIcon: {
    display: 'flex',
    alignItems: 'center'
  },
  hideMarginBottom: {
    marginBottom: 0
  }
}, { name: 'ContributionCalculator' });

export default ContributionCalculator;
